import "./organizationHome.scss";

import { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../features/helpers";
import { fetchOrganizationProfit, getOrganizationStartInfo, setModalAddClientStatus } from "../../../store/OrganizationSlice";
import {
    BanknoteArrowDownIcon,
    BanknoteArrowUpIcon,
    Building2,
    CalendarDays,
    Clock,
    LayoutDashboardIcon,
    TrendingUpIcon,
    UserCircle2,
    UserPlusIcon,
    PiggyBank,
    UsersIcon,
    WalletCards,
    X,
} from "lucide-react";
import { fetchWalletsByOrganizations } from "../../../store/WalletSlice";
import Price from "../../../components/price/price";
import { OperationStatus } from "../../../models/Transactions";
import Skeleton from "../../../components/skeleton/Skeleton";
import SkeletonLine from "../../../components/skeleton/SkeletonLine";
import { formatCurrencyBRL } from "../../../utils/format";
import LatestActivityCard from "../../../components/latest-activity-card/LatestActivityCard";
import ModalAddCLient from "../../../components/modal/modal-add-client/modal-add-client";
import BarChartDisplay from "../../../components/chart-bar/chatbar";
import { fetchDepositMonthly, fetchDeposits } from "../../../store/DepositsSlice";
import Wallets from "../../../components/wallets/wallets";
import HomeUsdt from "../../../components/chart/home-usdt";
import { fetchPurchases, fetchPurchasesByOrganizationToday, setPurchaseByOrganizationByDate } from "../../../store/PurchaseSlice";
import { fetchLastestTransactions } from "../../../store/TransactionSlice";
import { fetchLatest } from "../../../store/ReceiptsSlice";
import { fetchDepositsByUser, fetchUserWallets } from "../../../store/UserSlice";
import History from "../../../components/history/history";

type TabKey = "company" | "personal";

const OrganizationHome: React.FC = () => {

    const dispatch = useAppDispatch();

    const userInfo                      = useAppSelector( (state) => state.user.userInfo);
    const invoices                      = useAppSelector( (state) => state.invoice.organizationsWithdrawals);
    const wallets : Array<any> | null   = useAppSelector( (state) => state.wallet.organizationWallets);
    const profit                        = useAppSelector( (state) => state.organization.listProfit);
    const clients                       = useAppSelector( (state) => state.organization.clients);
    const showModal                     = useAppSelector( (state) => state.organization.modalAddClientStatus);

    // dados pessoais do owner logado
    const userWallets : Array<any> | null = useAppSelector( (state) => state.user.wallets);
    const userDeposits : Array<any>       = useAppSelector( (state) => state.user.deposits);

    const [sumProfit, setSumProfit]     = useState<string|null>(null);
    const [totalProfit, setTotalProfit] = useState<number|null>(null);

    const [filteredWallets, setFilteredWallet] = useState<Array<any> | null>();

    const [invoicesPending, setInvoicesPending] = useState<number|null>();
    const [totalPending, setTotalPending] = useState<string|null>('');

    // KPIs pessoais
    const [personalDeposited, setPersonalDeposited] = useState<number>(0);
    const [personalPending, setPersonalPending]     = useState<number>(0);
    const [personalLast, setPersonalLast]           = useState<string>('');

    const [activeTab, setActiveTab] = useState<TabKey>("company");

    // flags de "ja tentou carregar" — separa "ainda nao buscou" de "lista vazia"
    // (o estado inicial do redux e [] truthy, entao precisamos disso)
    const [companyLoaded, setCompanyLoaded]   = useState(false);
    const [personalLoaded, setPersonalLoaded] = useState(false);

    const companyFetchedRef  = useRef(false);
    const personalFetchedRef = useRef(false);

    // Carregamento inicial da aba Empresa (default)
    useEffect( () => {
        if (companyFetchedRef.current) return;
        companyFetchedRef.current = true;

        Promise.resolve(dispatch( fetchWalletsByOrganizations()))
            .finally(() => setCompanyLoaded(true));

        dispatch( getOrganizationStartInfo());
        dispatch( fetchLatest('all') )
        dispatch( fetchLatest('invoices') )
        dispatch( fetchLatest('deposits') )
        dispatch( fetchLatest('purchases') )
        dispatch( fetchLatest('payments') )
    }, [])

    // Carregamento lazy da aba "Minha conta" (1a vez que abrir)
    useEffect(() => {
        if (activeTab !== "personal") return;
        if (!userInfo?.id) return;
        if (personalFetchedRef.current) return;

        personalFetchedRef.current = true;

        Promise.all([
            Promise.resolve(dispatch(fetchUserWallets(userInfo.id))),
            Promise.resolve(dispatch(fetchDepositsByUser(userInfo.id))),
        ]).finally(() => setPersonalLoaded(true));
    }, [activeTab, userInfo?.id]);

    useEffect( () => {
        if (invoices) {
            let total = 0;
            let sumTotal = 0;
            if (invoices.length > 0) {
                invoices.forEach( invoice => {
                    if (invoice.status === OperationStatus.PENDING) {
                        total++;
                        sumTotal += Number(invoice.value)
                    }
                })
            }
            setInvoicesPending(total);
            setTotalPending(sumTotal.toString());
        }

    }, [invoices])

    useEffect ( () => {

        if (profit && profit.length > 0) {
            setTotalProfit(profit.length);
            let sum = 0;
            profit.forEach( (p:any) => {
                sum += Number(p.profit)
            });
            setSumProfit( formatCurrencyBRL(Number(sum.toFixed(2))) );
        } else {
            setSumProfit('0');
            setTotalProfit(0)
        }
    }, [profit])

    useEffect( () => {
        if (wallets) {
            const first = wallets.find(item => item.name === 'a');
            const rest  = wallets.filter(item => item.name !== 'a');

            const result = first ? [first, ...rest] : wallets;
            setFilteredWallet(result);
        }

    }, [wallets])

    // recalcula KPIs pessoais a partir dos depositos do owner
    useEffect(() => {
        if (!userDeposits) return;
        let deposited = 0;
        let pending = 0;
        userDeposits.forEach((d: any) => {
            if (d.status === "waiting") pending  += +d.value;
            else                         deposited += +d.value;
        });
        setPersonalDeposited(deposited);
        setPersonalPending(pending);
        if (userDeposits.length > 0) {
            setPersonalLast(userDeposits[userDeposits.length - 1].updatedAt);
        }
    }, [userDeposits]);

    const toggle = () => {
        dispatch(setModalAddClientStatus(true));
    }

    const personalBalance = userWallets?.[0]?.balance;

    // Wallet "profit" (lucro) fica numa linha separada; a linha principal
    // mostra o resto das carteiras, como antes da profit existir.
    const operationalWallets = filteredWallets?.filter(
        (w: any) => w.nickname?.toLowerCase() !== "profit"
    );
    const profitWallets = filteredWallets?.filter(
        (w: any) => w.nickname?.toLowerCase() === "profit"
    );

    return (
        <div className="organization-wrapper dashboard">

            <h1 className="page-title">
                <div className="text">
                    <div>
                        <LayoutDashboardIcon size={24} className="icon" /> Area da Empresa
                    </div>
                    <small>Dashboard de controle da empresa. Aqui voce pode ver tudo relacionado a voce.</small>
                </div>

                <div className="page-title-actions">
                    {/* Tabs Empresa / Minha conta */}
                    <div className="scope-tabs" role="tablist" aria-label="Escolha o escopo do dashboard">
                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeTab === "company"}
                            className={`scope-tab ${activeTab === "company" ? "active" : ""}`}
                            onClick={() => setActiveTab("company")}
                        >
                            <Building2 size={16} />
                            <span>Empresa</span>
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeTab === "personal"}
                            className={`scope-tab ${activeTab === "personal" ? "active" : ""}`}
                            onClick={() => setActiveTab("personal")}
                        >
                            <UserCircle2 size={16} />
                            <span>Minha conta</span>
                        </button>
                    </div>

                    {activeTab === "company" && (
                        <div className="button btn purple" onClick={toggle}>
                            <UserPlusIcon size={20} />Convidar Cliente
                        </div>
                    )}
                </div>
            </h1>

            {/* ============================================== */}
            {/* TAB: EMPRESA                                   */}
            {/* ============================================== */}
            {activeTab === "company" && (
                <div className="tab-panel">
                    <div className="row">
                        <div className="column w-50">
                            <h2 className="section-title">
                                <Building2 size={18} /> Carteiras da empresa
                                {operationalWallets && operationalWallets.length > 0 && (
                                    <span className="count-badge">{operationalWallets.length}</span>
                                )}
                            </h2>

                            {!companyLoaded && <SkeletonLine height="160px" />}

                            {companyLoaded && operationalWallets && operationalWallets.length > 0 && (
                                <Wallets wallets={operationalWallets} />
                            )}

                            {companyLoaded && (!filteredWallets || filteredWallets.length === 0) && (
                                <div className="empty-state">
                                    <Building2 size={32} />
                                    <p>A empresa ainda nao possui carteiras.</p>
                                    <small>Solicite ao seu gerente de conta a criacao da primeira carteira da organizacao.</small>
                                </div>
                            )}

                            {companyLoaded && profitWallets && profitWallets.length > 0 && (
                                <>
                                    <h2 className="section-title">
                                        <PiggyBank size={18} /> Carteira de lucro
                                    </h2>
                                    <Wallets wallets={profitWallets} historyHref="/profit" />
                                </>
                            )}
                        </div>
                        <div className="column w-50">
                            <h2 className="section-title">
                                <TrendingUpIcon size={18} /> Historico de preco do USDT
                            </h2>
                            <div className="chart-bar">
                                <HomeUsdt />
                            </div>
                        </div>
                    </div>

                    <h2 className="section-title">
                        <CalendarDays size={18} /> Info mensal da empresa
                    </h2>

                    <div className="row flex">

                        <div className="data-cards deposits">
                            <div className="icon">
                                <BanknoteArrowDownIcon size={24} />
                            </div>
                            <h2>
                                Pedidos de Saques:
                                <span>{invoicesPending !== null ? invoicesPending : <SkeletonLine height="10" />}</span>
                            </h2>
                            <div className="value">
                                total: {totalPending ? formatCurrencyBRL(Number(totalPending)) : <SkeletonLine height="10" />}
                            </div>
                        </div>

                        <div className="data-cards profit">
                            <div className="icon">
                                <TrendingUpIcon size={24} />
                            </div>
                            <h2>
                                Lucro Mensal:
                                <span>{sumProfit !== null ? sumProfit : <SkeletonLine height="10" />}</span>
                            </h2>
                            <div className="value">
                                {totalProfit !== null ? totalProfit : <SkeletonLine height="10" />} compras
                            </div>
                        </div>

                        <div className="data-cards clients">
                            <div className="icon">
                                <UsersIcon size={24} />
                            </div>
                            <h2>
                                Clientes Ativos:
                                <span>{clients ? clients.length : <SkeletonLine height="10" />}</span>
                            </h2>
                            <div className="value"></div>
                        </div>

                    </div>

                    <div className="rows">
                        <LatestActivityCard />
                    </div>
                </div>
            )}

            {activeTab === "personal" && (
                <div className="tab-panel">

                    {/* Hero pessoal */}
                    <section className="hero-balance personal">
                        <div className="hero-bg" aria-hidden="true" />
                        <div className="hero-content">
                            <div className="hero-text">
                                <span className="label">
                                    <UserCircle2 size={14} /> {userInfo?.name || "Sua conta"} — saldo pessoal
                                </span>
                                <strong className="value">
                                    {personalLoaded
                                        ? formatCurrencyBRL(Number(personalBalance ?? 0))
                                        : <SkeletonLine height="44px" />}
                                </strong>
                                <div className="hero-meta">
                                    <span className="pending">
                                        <Clock size={12} /> pendente:&nbsp;
                                        <b>{formatCurrencyBRL(personalPending)}</b>
                                    </span>
                                    {personalLast && (
                                        <span className="last-tx">
                                            ultima transacao em <b>{personalLast}</b>
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="hero-actions">
                                <button className="btn ghost">
                                    Solicitar <BanknoteArrowUpIcon size={18} />
                                </button>
                                <button className="btn buy">
                                    Sacar <BanknoteArrowDownIcon size={18} />
                                </button>
                            </div>
                        </div>
                    </section>

                    {/* KPIs pessoais */}
                    <section className="kpi-row">
                        <div className="kpi kpi-success">
                            <div className="kpi-icon"><BanknoteArrowUpIcon size={18} /></div>
                            <div className="kpi-info">
                                <small>Total depositado</small>
                                <strong>{personalLoaded ? formatCurrencyBRL(personalDeposited) : <SkeletonLine height="22px" />}</strong>
                            </div>
                        </div>
                        <div className="kpi kpi-warn">
                            <div className="kpi-icon"><Clock size={18} /></div>
                            <div className="kpi-info">
                                <small>Aguardando confirmacao</small>
                                <strong>{personalLoaded ? formatCurrencyBRL(personalPending) : <SkeletonLine height="22px" />}</strong>
                            </div>
                        </div>
                        <div className="kpi kpi-info">
                            <div className="kpi-icon"><WalletCards size={18} /></div>
                            <div className="kpi-info">
                                <small>Carteiras pessoais</small>
                                <strong>{personalLoaded ? (userWallets?.length ?? 0) : <SkeletonLine height="22px" />}</strong>
                            </div>
                        </div>
                    </section>

                    <div className="row">
                        <div className="column w-50">
                            <h2 className="section-title">
                                <UserCircle2 size={18} /> Minhas carteiras
                                {userWallets && userWallets.length > 0 && (
                                    <span className="count-badge">{userWallets.length}</span>
                                )}
                            </h2>

                            {!personalLoaded && <SkeletonLine height="160px" />}

                            {personalLoaded && userWallets && userWallets.length > 0 && (
                                <Wallets wallets={userWallets} />
                            )}

                            {personalLoaded && (!userWallets || userWallets.length === 0) && (
                                <div className="empty-state">
                                    <WalletCards size={32} />
                                    <p>Voce ainda nao tem carteiras pessoais.</p>
                                    <small>Carteiras pessoais sao separadas das carteiras da empresa.</small>
                                </div>
                            )}
                        </div>

                        <div className="column w-50">
                            <h2 className="section-title">
                                <Clock size={18} /> Minhas transacoes recentes
                            </h2>
                            <div className="personal-history">
                                {!personalLoaded && <SkeletonLine height="120px" />}

                                {personalLoaded && userDeposits && userDeposits.length > 0 && (
                                    <History list={userDeposits} showMore={false} />
                                )}

                                {personalLoaded && (!userDeposits || userDeposits.length === 0) && (
                                    <div className="empty-state">
                                        <Clock size={32} />
                                        <p>Sem transacoes pessoais ainda.</p>
                                        <small>Quando voce fizer um deposito ou saque pessoal ele aparece aqui.</small>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                </div>
            )}

            {showModal && <ModalAddCLient />}

        </div>
    )
}

export default OrganizationHome;
